import React, { FC, useEffect, useMemo } from 'react';
import { NavLink } from 'react-router-dom';

import usePdfLocalitiesPagination from '~/src/hooks/usePdfLocalitiesPagination';
import {
  DrillDownPanelLink,
  DrillDownPanelButton,
} from '~/src/components/DrillDownPanel';
import { buildCategoryFolders } from '~/src/containers/FederalFormsWithLocalitiesContainer';
import { normalizeLocality } from './StateRegionTable';

const MAX_PREVIEW_CATEGORIES = 2;

const FEDERAL_FORMS_QUERY = {
  facets: {
    filter_category: 'FEDERAL',
  },
  group_by: 'filter_category,locality',
  query_by: 'locality',
};

interface Props {
  routeUrl?: string;
  actionType?: 'link' | 'button';
  onCategorySelect?: (categoryLocality: string) => void;
  onViewAll?: () => void;
}

const FederalCategoriesPreviewSection: FC<Props> = ({
  routeUrl = '/library/court-forms',
  actionType = 'link',
  onCategorySelect,
  onViewAll,
}) => {
  const { next, loading, data } =
    usePdfLocalitiesPagination(FEDERAL_FORMS_QUERY);

  useEffect(() => {
    if (!loading && data.length === 0) {
      next();
    }
  }, [loading, data.length, next]);

  const categoryFolders = useMemo(() => buildCategoryFolders(data), [data]);

  const displayedCategories = categoryFolders.slice(0, MAX_PREVIEW_CATEGORIES);
  const hasMoreCategories = categoryFolders.length > MAX_PREVIEW_CATEGORIES;

  if (!loading && categoryFolders.length === 0) {
    return null;
  }

  const handleGetCategoryUrl = (category: {
    filterCategory: string;
    locality: string;
  }) => {
    const normalizedLocality = normalizeLocality(category.locality);
    return `${routeUrl}/FEDERAL/${normalizedLocality}/form-listing`;
  };

  return (
    <div className="my-5">
      <div className="p-2 font-medium">Federal</div>

      <div className="flex flex-col bg-white rounded-lg overflow-hidden border border-slate-200">
        {loading && displayedCategories.length === 0 ? (
          <div className="p-4 text-gray-500">Loading...</div>
        ) : (
          <>
            {displayedCategories.map((category) =>
              actionType === 'button' ? (
                <DrillDownPanelButton
                  key={category.locality}
                  label={category.locality}
                  handleClick={() => onCategorySelect?.(category.locality)}
                />
              ) : (
                <DrillDownPanelLink
                  key={category.locality}
                  label={category.locality}
                  linkUrl={handleGetCategoryUrl(category)}
                />
              ),
            )}
            {hasMoreCategories &&
              (actionType === 'button' ? (
                <button
                  type="button"
                  onClick={onViewAll}
                  className="p-4 text-center text-blue-600 hover:text-blue-800 hover:bg-gray-50 border-t border-slate-200"
                >
                  View more
                </button>
              ) : (
                <NavLink
                  to={`${routeUrl}/federal_USCIS`}
                  className="p-4 text-center text-blue-600 hover:text-blue-800 hover:bg-gray-50 border-t border-slate-200"
                >
                  View more
                </NavLink>
              ))}
          </>
        )}
      </div>
    </div>
  );
};

export default FederalCategoriesPreviewSection;
